import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ApprovalState, Client, EquipmentSafeguard, EquipmentTabDto, FacilityEnum, FacilityOwnerDto, GePredixEquipmentDto, PercentOverdue, ReportType, TaskFrequency } from '@fdm/api-client';
import { MaterialModule } from '@fdm/material';
import { LoaderService } from '@fdm/services';
import { getErrorMessage } from '@fdm/utils';
import { ToastrService } from 'ngx-toastr';
import {
  catchError,
  combineLatest,
  distinctUntilChanged,
  filter,
  finalize,
  first,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { ApprovalFlowService, FormValidationService, NavigationService, ReportFormPage, ReportsBulkSaveService, ReportTask } from '../../../../services';
import { E1TableChange, E1TableComponent } from './components/e1-table/e1-table.component';
import { EquipmentDescriptionComponent } from './components/equipment-description/equipment-description.component';
import { EquipmentDetailsComponent } from './components/equipment-details/equipment-details.component';
import { GEPredixTableComponent } from './components/ge-predix-table/ge-predix-table.component';
import { displayNames } from './equipment-tab.constants';
import { PolicyService } from '../../../../services/roles/policy.service';
import { ReportForm } from '../../report-form';
import { AngularEditorConfig } from '@kolkov/angular-editor';
import { createEditorConfigs } from '../../shared/richtext-editor-config-helper';

const CONSEQUENCE_RANKING_VALUES = [
  'NotAssigned',
  'IC1',
  'IC2',
  'IC3',
  'IC4',
  'IC5',
  'IC6',
  'AC1',
  'AC2',
  'AC3',
  'AC4',
  'AC5',
  'AC6',
];

const consequenceRankingValidator = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;

  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value !== 'string') {
    return { invalidConsequenceRanking: true };
  }

  const normalizedValue = value.trim();

  if (!normalizedValue) {
    return { invalidConsequenceRanking: true };
  }

  const isKnownRanking = CONSEQUENCE_RANKING_VALUES.some(
    item => item.toUpperCase() === normalizedValue.toUpperCase()
  );

  return isKnownRanking
    ? null
    : { invalidConsequenceRanking: true };
};

const equipmentRequiredValidator = (control: AbstractControl): ValidationErrors | null => {
  const e1Count = (control.get('e1DataEquipments') as FormArray | null)?.length ?? 0;
  const predixCount = (control.get('predixDataEquipments') as FormArray | null)?.length ?? 0;

  return e1Count > 0 || predixCount > 0
    ? null
    : { equipmentRequired: true };
};

@Component({
  selector: 'app-equipment-tab',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MaterialModule,

    // Components
    EquipmentDetailsComponent,
    EquipmentDescriptionComponent,
    E1TableComponent,
    GEPredixTableComponent,
  ],
  templateUrl: './equipment-tab.component.html',
  styleUrl: './equipment-tab.component.scss',
})
export class EquipmentTabComponent extends ReportForm implements OnInit {
  @Input() data: any = {};

  @Input() readonly: boolean = false;

  private _route = inject(ActivatedRoute);
  private _router = inject(Router);
  private _navigationService = inject(NavigationService);
  private _loader = inject(LoaderService);
  private _apiClient = inject(Client);
  private _destroyRef = inject(DestroyRef);
  private _toastrService = inject(ToastrService);
  private _bulkSave = inject(ReportsBulkSaveService);
  private _policyService = inject(PolicyService);
  private _formValidationService = inject(FormValidationService);
  private _approvalFlow = inject(ApprovalFlowService);

  private _fb: FormBuilder = new FormBuilder();
  public readonly displayNames = displayNames;

  public form: FormGroup = this._fb.group(
    {
      id: [''],
      facility: ['', Validators.required],
      facilityOwner: this._fb.group({
        facility: ['', Validators.required],
        formattedFacility: [''],
        facilityOwnerId: [''],
        sharedEmail: [''],
        name: [''],
      }),
      facilityOwnerId: [null],
      area: [''],
      isEquipmentSafeguard: [EquipmentSafeguard.Unknown, Validators.required],
      taskFrequency: [''],
      percentOverdue: [''],
      consequenceRanking: [null, [Validators.required, consequenceRankingValidator]],
      standbyMode: [false],
      assignedManagementAuthorizationLevel: [''],
      justification: ['', [Validators.maxLength(20000)]],
      additionalInformationForEquipmentEN: ['', [Validators.maxLength(20000)]],
      additionalInformationForEquipmentRU: ['', [Validators.maxLength(20000)]],
      additionalInformationForEquipmentKZ: ['', [Validators.maxLength(20000)]],
      e1DataEquipments: this._fb.array([]),
      predixDataEquipments: this._fb.array([]),
      generalTabId: [''],
    },
    { validators: [equipmentRequiredValidator] }
  );

  private _closeReport = toSignal(this._store.closeReport$);
  private currentStep = toSignal(this._store.page$);
  private _general = toSignal(this._store.general$);

  public get taskFrequency() {
    return this.form.get('taskFrequency') as FormControl<TaskFrequency | null>;
  }

  public get percentOverdue() {
    return this.form.get('percentOverdue') as FormControl<PercentOverdue | null>;
  }

  public get consequenceRanking() {
    return this.form.get('consequenceRanking') as FormControl<string | null>;
  }

  public get isEquipmentSafeguard() {
    return this.form.get('isEquipmentSafeguard') as FormControl<EquipmentSafeguard | null>;
  }

  public get e1Table() {
    return this.form.get('e1DataEquipments') as FormArray<FormGroup<any>>;
  }

  public get gePredixTable() {
    return this.form.get('predixDataEquipments') as FormArray<FormGroup<any>>;
  }

  public get authLevel() {
    return this.form.get('assignedManagementAuthorizationLevel') as FormControl<any>;
  }

  public get standbyMode() {
    return this.form.get('standbyMode') as FormControl<any>;
  }

  public get justification() {
    return this.form.get('justification') as FormControl<any>;
  }

  public get step() {
    return ReportFormPage.EQUIPMENTS;
  }

  protected override getStepValidity(): boolean {
    return this._isEquipmentStepValid();
  }


  private get component() {
    return 'EquipmentTabComponent';
  }

  private _reportType = toSignal(this._store.reportType$);

  isTDR = computed(() => {
    const reportType = this._reportType();

    return reportType === ReportType.TaskDeferral;
  });

  private _facilityOwners: FacilityOwnerDto[] = [];
  private _isManualStandbyOverride = false;
  private _isLoadingData = false;
  private _lastRequestedGeneralTabId: string | null = null;

  private editorConfigs = createEditorConfigs(3, 'equipment');
  configEN: AngularEditorConfig = this.editorConfigs[0];
  configRU: AngularEditorConfig = this.editorConfigs[1];
  configKZ: AngularEditorConfig = this.editorConfigs[2];

  // These fields are display-only (locked icon in UI) and must be disabled
  // so Angular excludes them from form validation (disabled controls are always VALID)
  override readonlyFields = [
    'facility',
    'facilityOwner',
    'facilityOwnerId',
    'area',
    'assignedManagementAuthorizationLevel',
  ];

  override ngOnInit(): void {
    super.ngOnInit();
    this._debug('ngOnInit', {
      step: this.step,
      generalTabId: this.form.get('generalTabId')?.value,
    });

    // Pre-populate from store synchronously before registering the form so that
    // getInvalidForms() does not see an empty/invalid form on the first CD cycle.
    const generalTabId = this.form.get('generalTabId')?.value;
    const storedEquipment = this._normalizeEquipmentSafeguard(this._store.equipment());
    if (storedEquipment && storedEquipment.generalTabId === generalTabId) {
      this.form.patchValue(storedEquipment, { emitEvent: false });
    }

    // These controls are always read-only for this tab. Disable them immediately
    // so the form does not look invalid before the user opens the Equipment tab.
    this._applyReadonlyFieldState();
    this.form.updateValueAndValidity({ emitEvent: false });

    this._initTriggers();
    this._formValidationService.registerForm(ReportFormPage.EQUIPMENTS, this.form);
  }
   protected override onPolicyOrFormStateChanged() {
    this.richTextFieldPolicy();
  }
  richTextFieldPolicy() {
    this.configEN.editable = this.isFieldEditable('additionalInformationForEquipmentEN');
    this.configRU.editable = this.isFieldEditable('additionalInformationForEquipmentRU');
    this.configKZ.editable = this.isFieldEditable('additionalInformationForEquipmentKZ');
  }

  private _save() {
    const formData = this.form.getRawValue();
    const generalTabId = formData.generalTabId ?? this.form.get('generalTabId')?.value;
    const resolvedId = this._resolveEquipmentTabId(formData.id, generalTabId);

    this._debug('save:start', {
      id: resolvedId,
      generalTabId,
      consequenceRanking: formData.consequenceRanking,
      e1Count: formData.e1DataEquipments?.length ?? 0,
      predixCount: formData.predixDataEquipments?.length ?? 0,
    });

    let facilityOwner = formData.facilityOwner;
    if (formData.facilityOwnerId && !facilityOwner) {
      facilityOwner = this._facilityOwners.find(item => item.id === formData.facilityOwnerId);
    }

    const payload = EquipmentTabDto.fromJS({
      ...formData,
      id: resolvedId,
      justification: formData.justification ? formData.justification : undefined,
      percentOverdue: formData.percentOverdue ? formData.percentOverdue : undefined,
      taskFrequency: formData.taskFrequency ? formData.taskFrequency : undefined,
      assignedManagementAuthorizationLevel: formData.assignedManagementAuthorizationLevel ? formData.assignedManagementAuthorizationLevel : undefined,
      additionalInformationForEquipmentEN: formData.additionalInformationForEquipmentEN ?? '',
      additionalInformationForEquipmentKZ: formData.additionalInformationForEquipmentKZ ?? '',
      additionalInformationForEquipmentRU: formData.additionalInformationForEquipmentRU ?? '',
      facilityOwnerId: formData.facilityOwnerId ?? undefined,
      facilityOwner: facilityOwner 
        ? FacilityOwnerDto.fromJS({
          ...facilityOwner,
          id: facilityOwner.facilityOwnerId || formData.facilityOwnerId || undefined
        }) 
        : undefined,
    });

    return this._handleSave(payload)
      .pipe(
        tap(() => {
          /**
           * TODO
           * use component store for fetching general, equipment, approval flow data
           */

          if (this._closeReport()) {
            this._navigationService.navigateToListPage();
          }
        }),
        switchMap(() => this._getDataById()),
        first()
      )
  }

  facilityNameMappping(facility?: string) {
    if (facility === 'SGI/SGP') {
      return FacilityEnum.SGISGP;
    } else if (facility === 'Non production facility') {
      return FacilityEnum.NonProductionFacility;
    } else if (facility === 'Not assigned') {
      return FacilityEnum.NotAssigned;
    } else if (facility === 'Tengiz facilities maintenance') {
      return FacilityEnum.TengizFacilitiesMaintenance;
    }

    return '';
  }

  public getE1Data(data: E1TableChange, emitEvent = true) {
    this.e1Table.clear({ emitEvent });
    data.equipments.map((item) => this.e1Table.push(this._fb.group(item), { emitEvent }));

    // const facilityOwner = this._facilityOwners.find(item => item.facility === data.equipments?.[0]?.facility);
    const facilityOwner = this._facilityOwners.find(item => {
      return item.facility === data.equipments?.[0]?.facility || item.facility === this.facilityNameMappping(data.equipments?.[0]?.facility);
    });

    if (facilityOwner) {
      this.form.patchValue({
        facilityOwner: {
          ...facilityOwner,
          facilityOwnerId: facilityOwner?.id ?? undefined,
        },
        facilityOwnerId: facilityOwner?.id,
      }, { emitEvent });
    } else {
      this.form.patchValue({
        facilityOwnerId: undefined,
        facilityOwner: {
          facility: '',
          formattedFacility: '',
          facilityOwnerId: '',
          sharedEmail: '',
          name: '',
        }
      }, { emitEvent });
    }

    this._store.setEquipment(EquipmentTabDto.fromJS(this.form.getRawValue()));
    this._cacheDraft(this.form.get('generalTabId')?.value ?? this.form.value.generalTabId, this.form.getRawValue());
    this._debug('getE1Data', {
      emitEvent,
      e1Count: this.e1Table.length,
      cr: this.consequenceRanking.value,
      facility: this.form.get('facility')?.value,
    });

    // Adding/removing equipment is a genuine user change; mark dirty so the save task enqueues.
    this.form.markAsDirty();

    // Update form validity to reflect equipment change
    this.form.updateValueAndValidity({ emitEvent: false });

    const isFormValid = this._isEquipmentStepValid();

    if (isFormValid) {
      const payload: ReportTask = {
        page: this.step,
        request: this._save()
      };

      if (this._bulkSave.includes(this.step)) {
        this._bulkSave.update(payload);
      } else {
        this._bulkSave.add(payload);
      }

      if (!this.canEdit) {
        this._bulkSave.save();
      }

    }

    // Update page validity to ensure the store reflects the current form state
    this._store.setPageValidity({ page: this.step, isStepValid: this._isEquipmentStepValid() });
  }

  public getPredixData(data: GePredixEquipmentDto[], emitEvent = true) {
    this.gePredixTable.clear({ emitEvent });
    data.map((item) => this.gePredixTable.push(this._fb.group(item), { emitEvent }));

    this._store.setEquipment(EquipmentTabDto.fromJS(this.form.getRawValue()));
    this._cacheDraft(this.form.get('generalTabId')?.value ?? this.form.value.generalTabId, this.form.getRawValue());
    this._debug('getPredixData', {
      emitEvent,
      predixCount: this.gePredixTable.length,
      cr: this.consequenceRanking.value,
    });

    // Adding/removing equipment is a genuine user change; mark dirty so the save task enqueues.
    this.form.markAsDirty();

    // Update form validity to reflect equipment change
    this.form.updateValueAndValidity({ emitEvent: false });

    const isFormValid = this._isEquipmentStepValid();

    if (isFormValid) {
      const payload: ReportTask = {
        page: this.step,
        request: this._save()
      };

      if (this._bulkSave.includes(this.step)) {
        this._bulkSave.update(payload);
      } else {
        this._bulkSave.add(payload);
      }

      if (!this.canEdit) {
        this._bulkSave.save();
      }
      // this._bulkSave.save();
    }

    // Update page validity to ensure the store reflects the current form state
    this._store.setPageValidity({ page: this.step, isStepValid: this._isEquipmentStepValid() });
  }

  private _handleSave(payload: any) {
    const errorCb = (err: any) => {
      this._store.reset();

      if ('error' in err) {
        this._toastrService.warning(err.error);
      }

      return of(err);
    }

    return payload.id
      ? this._apiClient.equipmentTabPUT(payload.id, payload).pipe(
          map(() => payload.id),
          tap(() => {
            // hide notification for closing
            if (this._general()?.currentState !== ApprovalState.ValidationAndVerificationCompleted) {
              this._toastrService.success('Equipments updated successfully!');
            }

            this._approvalFlow.flowEventsUpdated.next();

            this._store.reset();
          }),
          catchError(errorCb)
        )
      : this._apiClient.equipmentTabPOST(payload).pipe(
          tap((id) => {
            if (id) {
              this.form.patchValue({ id }, { emitEvent: false });

              const generalTabId = this.form.get('generalTabId')?.value ?? payload.generalTabId;
              this._store.setEquipment(EquipmentTabDto.fromJS({
                ...this.form.getRawValue(),
                id,
                generalTabId,
              }));
              this._cacheDraft(generalTabId, this.form.getRawValue());
              this._debug('save:post_id_assigned', { id, generalTabId });
            }
          }),
          tap(() => {
            this._toastrService.success('Equipments saved successfully!');
            
            this._approvalFlow.flowEventsUpdated.next();

            this._store.reset();
          }),
          catchError(errorCb)
        );
  }

  private _resolveEquipmentTabId(formId: number | string | null | undefined, generalTabId: string | undefined): number | undefined {
    const directId = this._toNumberOrUndefined(formId);

    if (directId) {
      return directId;
    }

    const storedEquipment = this._store.equipment();

    if (storedEquipment && storedEquipment.generalTabId === generalTabId) {
      const storedId = this._toNumberOrUndefined(storedEquipment.id);

      if (storedId) {
        return storedId;
      }
    }

    const cachedEquipment = this._readDraft(generalTabId);

    if (cachedEquipment && cachedEquipment.generalTabId === generalTabId) {
      const cachedId = this._toNumberOrUndefined(cachedEquipment.id);

      if (cachedId) {
        return cachedId;
      }
    }

    return undefined;
  }

  private _toNumberOrUndefined(value: unknown): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }

    const parsed = typeof value === 'number' ? value : Number(value);

    return Number.isFinite(parsed) ? parsed : undefined;
  }

  private _getAuthLevel(
    taskFrequency: TaskFrequency,
    percentOverdue: PercentOverdue,
    consequenceRanking: string | null
  ) {
    let data;

    if (
      consequenceRanking &&
      ['IC1', 'IC2', 'AC1', 'AC2'].includes(consequenceRanking)
    ) {
      data = {
        [TaskFrequency.GreaterOrEqual5Years]: {
          [PercentOverdue.LessThan10]: 'B',
          [PercentOverdue.TenToLessThan20]: 'C',
          [PercentOverdue.TwentyToLessThan30]: 'D',
          [PercentOverdue.ThirtyToLessThan40]: 'D',
          [PercentOverdue.Forty]: 'D',
        },
        [TaskFrequency.ThreeToLessThanFiveYears]: {
          [PercentOverdue.LessThan10]: 'A',
          [PercentOverdue.TenToLessThan20]: 'B',
          [PercentOverdue.TwentyToLessThan30]: 'C',
          [PercentOverdue.ThirtyToLessThan40]: 'D',
          [PercentOverdue.Forty]: 'D',
        },
        [TaskFrequency.OneToLessThanThreeYears]: {
          [PercentOverdue.LessThan10]: 'A',
          [PercentOverdue.TenToLessThan20]: 'A',
          [PercentOverdue.TwentyToLessThan30]: 'B',
          [PercentOverdue.ThirtyToLessThan40]: 'C',
          [PercentOverdue.Forty]: 'D',
        },
        [TaskFrequency.LessThanOneYear]: {
          [PercentOverdue.LessThan10]: 'A',
          [PercentOverdue.TenToLessThan20]: 'A',
          [PercentOverdue.TwentyToLessThan30]: 'A',
          [PercentOverdue.ThirtyToLessThan40]: 'B',
          [PercentOverdue.Forty]: 'C',
        },
      };
    } else if (consequenceRanking === 'IC3') {
      data = {
        [TaskFrequency.GreaterOrEqual5Years]: {
          [PercentOverdue.LessThan10]: 'B',
          [PercentOverdue.TenToLessThan20]: 'B',
          [PercentOverdue.TwentyToLessThan30]: 'B',
          [PercentOverdue.ThirtyToLessThan40]: 'B',
          [PercentOverdue.Forty]: 'B',
        },
        [TaskFrequency.ThreeToLessThanFiveYears]: {
          [PercentOverdue.LessThan10]: 'A',
          [PercentOverdue.TenToLessThan20]: 'B',
          [PercentOverdue.TwentyToLessThan30]: 'B',
          [PercentOverdue.ThirtyToLessThan40]: 'B',
          [PercentOverdue.Forty]: 'B',
        },
        [TaskFrequency.OneToLessThanThreeYears]: {
          [PercentOverdue.LessThan10]: 'A',
          [PercentOverdue.TenToLessThan20]: 'A',
          [PercentOverdue.TwentyToLessThan30]: 'B',
          [PercentOverdue.ThirtyToLessThan40]: 'B',
          [PercentOverdue.Forty]: 'B',
        },
        [TaskFrequency.LessThanOneYear]: {
          [PercentOverdue.LessThan10]: 'A',
          [PercentOverdue.TenToLessThan20]: 'A',
          [PercentOverdue.TwentyToLessThan30]: 'A',
          [PercentOverdue.ThirtyToLessThan40]: 'B',
          [PercentOverdue.Forty]: 'B',
        },
      };
    }

    return data ? data[taskFrequency][percentOverdue] : 'A';
  }

  private _getDataById(externalGeneralTabId?: string | null) {
    const generalTabId = externalGeneralTabId ?? this.form.value.generalTabId;
    const cachedEquipment = this._normalizeEquipmentSafeguard(this._readDraft(generalTabId));

    const storedEquipment = this._normalizeEquipmentSafeguard(this._store.equipment());
    const storeHasRequired = this._hasRequiredEquipmentFields(storedEquipment);
    const cacheHasRequired = this._hasRequiredEquipmentFields(cachedEquipment);

    this._debug('getDataById:start', {
      generalTabId,
      hasStoreEquipment: !!this._store.equipment(),
      hasCachedEquipment: !!cachedEquipment,
      formDirty: this.form.dirty,
    });

    if (generalTabId) {
      if (storedEquipment && storedEquipment.generalTabId === generalTabId && storeHasRequired) {
        this._isLoadingData = true;

        this.form.patchValue(storedEquipment, { emitEvent: false });
        this._validateJustification();

        this.e1Table.clear({ emitEvent: false });
        storedEquipment.e1DataEquipments?.forEach((item) => this.e1Table.push(this._fb.group(item), { emitEvent: false }));

        this.gePredixTable.clear({ emitEvent: false });
        storedEquipment.predixDataEquipments?.forEach((item) => this.gePredixTable.push(this._fb.group(item), { emitEvent: false }));

        this._isLoadingData = false;
        this.form.updateValueAndValidity({ emitEvent: false });
        this._store.setPageValidity({ page: this.step, isStepValid: this._isEquipmentStepValid() });
        this._cacheDraft(generalTabId, this.form.getRawValue());
        this._debug('getDataById:restored_from_store', {
          cr: this.consequenceRanking.value,
          e1Count: this.e1Table.length,
          predixCount: this.gePredixTable.length,
        });

        return of(storedEquipment);
      }

      if (storedEquipment && storedEquipment.generalTabId === generalTabId && !storeHasRequired) {
      }

      if (cachedEquipment && cacheHasRequired) {
        this._isLoadingData = true;

        this.form.patchValue(cachedEquipment, { emitEvent: false });
        this._validateJustification();

        this.e1Table.clear({ emitEvent: false });
        cachedEquipment.e1DataEquipments?.forEach((item) => this.e1Table.push(this._fb.group(item), { emitEvent: false }));

        this.gePredixTable.clear({ emitEvent: false });
        cachedEquipment.predixDataEquipments?.forEach((item) => this.gePredixTable.push(this._fb.group(item), { emitEvent: false }));

        this._isLoadingData = false;
        this.form.updateValueAndValidity({ emitEvent: false });
        this._store.setEquipment(EquipmentTabDto.fromJS(this.form.getRawValue()));
        this._store.setPageValidity({ page: this.step, isStepValid: this._isEquipmentStepValid() });

        this._debug('getDataById:restored_from_cache', {
          cr: this.consequenceRanking.value,
          e1Count: this.e1Table.length,
          predixCount: this.gePredixTable.length,
        });

        return of(cachedEquipment);
      }

      if (cachedEquipment && !cacheHasRequired) {
      }

      this._lastRequestedGeneralTabId = generalTabId;
      return this._apiClient.byGeneralTab2(generalTabId).pipe(
        tap((data) => {
          if (data) {
            // Set flag to prevent _defineRanking from overwriting CR while loading tables
            this._isLoadingData = true;

            const storedEquipment = this._store.equipment();
            const fallbackEquipment = this._hasRequiredEquipmentFields(storedEquipment)
              ? storedEquipment
              : (this._hasRequiredEquipmentFields(cachedEquipment) ? cachedEquipment : (storedEquipment ?? cachedEquipment));
            
            // Normalize CR in the data object BEFORE patching the form
            // This prevents form.valid from going true with invalid CR during data load
            const normalizedCR = this._normalizeConsequenceRanking(data.consequenceRanking);
            const storedCR = this._normalizeConsequenceRanking(storedEquipment?.consequenceRanking);
            
            // If server returns empty CR, restore the last known draft value from store
            if (!normalizedCR && storedCR) {
              data.consequenceRanking = storedCR;
            } else {
              data.consequenceRanking = normalizedCR || undefined;
            }

            // Restore other draft fields if the server response is empty
            data.facility = data.facility || fallbackEquipment?.facility;
            data.facilityOwner = data.facilityOwner || fallbackEquipment?.facilityOwner;
            data.facilityOwnerId = data.facilityOwnerId || fallbackEquipment?.facilityOwnerId;
            data.area = data.area || fallbackEquipment?.area;
            data.taskFrequency = data.taskFrequency || fallbackEquipment?.taskFrequency;
            data.percentOverdue = data.percentOverdue || fallbackEquipment?.percentOverdue;
            data.isEquipmentSafeguard = data.isEquipmentSafeguard ?? fallbackEquipment?.isEquipmentSafeguard ?? EquipmentSafeguard.Unknown;
            data.standbyMode = data.standbyMode ?? fallbackEquipment?.standbyMode;
            data.assignedManagementAuthorizationLevel =
              data.assignedManagementAuthorizationLevel || fallbackEquipment?.assignedManagementAuthorizationLevel;
            data.justification = data.justification || fallbackEquipment?.justification;
            data.additionalInformationForEquipmentEN = data.additionalInformationForEquipmentEN || fallbackEquipment?.additionalInformationForEquipmentEN;
            data.additionalInformationForEquipmentRU = data.additionalInformationForEquipmentRU || fallbackEquipment?.additionalInformationForEquipmentRU;
            data.additionalInformationForEquipmentKZ = data.additionalInformationForEquipmentKZ || fallbackEquipment?.additionalInformationForEquipmentKZ;
            data.e1DataEquipments = data.e1DataEquipments?.length ? data.e1DataEquipments : fallbackEquipment?.e1DataEquipments;
            data.predixDataEquipments = data.predixDataEquipments?.length ? data.predixDataEquipments : fallbackEquipment?.predixDataEquipments;

            if (this._isEquipmentDataEmpty(data) && cachedEquipment) {
              this._debug('getDataById:api_empty_using_cache', {
                apiCr: data.consequenceRanking,
                apiE1Count: data.e1DataEquipments?.length ?? 0,
                apiPredixCount: data.predixDataEquipments?.length ?? 0,
              });

              data = EquipmentTabDto.fromJS(cachedEquipment);
              data.generalTabId = generalTabId;
            }

            this._debug('getDataById:api_payload', {
              cr: data.consequenceRanking,
              facility: data.facility,
              e1Count: data.e1DataEquipments?.length ?? 0,
              predixCount: data.predixDataEquipments?.length ?? 0,
            });

            // emitEvent:false prevents valueChanges firing per-field during load
            this.form.patchValue(data, { emitEvent: false });

            this._validateJustification();

            this.e1Table.clear({ emitEvent: false });
            data.e1DataEquipments?.forEach((item) => this.e1Table.push(this._fb.group(item), { emitEvent: false }));

            this.gePredixTable.clear({ emitEvent: false });
            data.predixDataEquipments?.forEach((item) => this.gePredixTable.push(this._fb.group(item), { emitEvent: false }));
            
            // Clear flag after table loading is complete
            this._isLoadingData = false;
          }

          // Final validity sync after all table patching (tables use emitEvent:false)
          this.form.updateValueAndValidity({ emitEvent: false });
          this._store.setPageValidity({ page: this.step, isStepValid: this._isEquipmentStepValid() });

          this._store.setEquipment(data);
          this._cacheDraft(generalTabId, this.form.getRawValue());
          this._debug('getDataById:done', {
            cr: this.consequenceRanking.value,
            e1Count: this.e1Table.length,
            predixCount: this.gePredixTable.length,
          });
        }),
        catchError((err) => {
          const status = err?.status ?? 'unknown';
          this._debug('getDataById:error', { err });

          // Prefer a complete source; fall back to any available partial data
          const completeFallback = this._hasRequiredEquipmentFields(storedEquipment)
            ? storedEquipment
            : (this._hasRequiredEquipmentFields(cachedEquipment) ? cachedEquipment : null);
          const partialFallback = storedEquipment ?? cachedEquipment;
          const fallbackEquipment = completeFallback ?? partialFallback;

          if (fallbackEquipment) {
            this._isLoadingData = true;

            const safeFallback = EquipmentTabDto.fromJS({
              ...fallbackEquipment,
              generalTabId,
            });

            this.form.patchValue(safeFallback, { emitEvent: false });
            this._validateJustification();

            this.e1Table.clear({ emitEvent: false });
            safeFallback.e1DataEquipments?.forEach((item) => this.e1Table.push(this._fb.group(item), { emitEvent: false }));

            this.gePredixTable.clear({ emitEvent: false });
            safeFallback.predixDataEquipments?.forEach((item) => this.gePredixTable.push(this._fb.group(item), { emitEvent: false }));

            this._isLoadingData = false;
            this.form.updateValueAndValidity({ emitEvent: false });
            this._store.setEquipment(safeFallback);

            // Only overwrite page validity when fallback has required fields.
            // If data is incomplete (API down, no local data yet), preserve whatever
            // validity was already in the store to avoid the tab going falsely red.
            if (completeFallback) {
              this._store.setPageValidity({ page: this.step, isStepValid: this._isEquipmentStepValid() });
            }

            this._cacheDraft(generalTabId, this.form.getRawValue());

            this._debug('getDataById:api_error_fallback_applied', {
              cr: this.consequenceRanking.value,
              e1Count: this.e1Table.length,
              predixCount: this.gePredixTable.length,
              status,
              hasRequiredFields: !!completeFallback,
            });

            return of(safeFallback);
          }

          // No local data at all — preserve existing page validity
          return of(null);
        }),
        finalize(() => {
          if (this._lastRequestedGeneralTabId === generalTabId) {
            this._lastRequestedGeneralTabId = null;
          }
          this._isLoadingData = false;
        })
      );
    }
    return of(null);
  }

  private _validateJustification(): void {
    const consequenceRanking = this.consequenceRanking.value;
    const isHCE = !!consequenceRanking && ['IC1', 'IC2', 'IC3', 'AC1', 'AC2'].includes(consequenceRanking);
    const isAnomalyReport = this._reportType() === ReportType.AnomalyReport;
    // For Anomaly Report: justification is never required (always optional)
    // For all other report types: justification is required when NOT HCE
    const shouldRequireJustification = isAnomalyReport ? false : !isHCE;

    this.justification.setValidators(
      shouldRequireJustification
        ? [Validators.required, Validators.maxLength(20000)]
        : [Validators.maxLength(20000)]
    );
    this.justification.updateValueAndValidity({ emitEvent: false });
    // Recalculate parent form so form.valid reflects updated justification validators
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  private _normalizeConsequenceRanking(value: unknown): string | null {
    if (typeof value !== 'string') {
      return null;
    }

    const normalizedValue = value.trim();

    if (!normalizedValue) {
      return null;
    }

    const canonicalRanking = CONSEQUENCE_RANKING_VALUES.find(
      item => item.toUpperCase() === normalizedValue.toUpperCase()
    );

    return canonicalRanking ?? null;
  }

  private _isEquipmentStepValid(): boolean {
    const normalizedConsequenceRanking = this._normalizeConsequenceRanking(this.consequenceRanking.value);
    return this.form.valid && !!normalizedConsequenceRanking;
  }

  private _hasEquipment(): boolean {
    const e1Count = this.e1Table.length ?? 0;
    const predixCount = this.gePredixTable.length ?? 0;
    return e1Count > 0 || predixCount > 0;
  }

  public hasEquipment(): boolean {
    return this._hasEquipment();
  }

  private _initTriggers(): void {
    combineLatest([
      this._route.paramMap,
      this._store.general$
    ])
      .pipe(
        map(([params, general]) => ({
          generalTabId: params.get('id'),
          generalId: general?.id ?? null,
        })),
        distinctUntilChanged((prev, curr) =>
          prev.generalTabId === curr.generalTabId && prev.generalId === curr.generalId
        ),
        filter(({ generalTabId }) => !!generalTabId),
        filter(({ generalTabId }) =>
          !this.form.dirty &&
          !this._isLoadingData &&
          this._lastRequestedGeneralTabId !== generalTabId
        ),
        tap(() => this._loader.startProcessing()),
        switchMap(({ generalTabId }) => {
          const resolvedGeneralTabId = generalTabId as string;

          this._debug('initTriggers:route_or_general_changed', {
            generalTabId: resolvedGeneralTabId,
            formDirty: this.form.dirty,
            currentCr: this.consequenceRanking.value,
            e1Count: this.e1Table.length,
            predixCount: this.gePredixTable.length,
          });

          this.form.get('generalTabId')?.setValue(resolvedGeneralTabId, { emitEvent: false });

          return this._getDataById(resolvedGeneralTabId);
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => this._loader.stopProcessing());

    combineLatest([
      this.taskFrequency.valueChanges,
      this.percentOverdue.valueChanges,
      this.consequenceRanking.valueChanges,
    ])
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(([taskFrequency, percentOverdue, consequenceRanking]) => {
        if (this._isManualStandbyOverride && this.standbyMode.value === true) {
          return;
        }

        if (!taskFrequency || !percentOverdue) {
          return;
        }

        const level = this._getAuthLevel(
          taskFrequency,
          percentOverdue,
          consequenceRanking
        );

        // this._validateJustification();

        this.authLevel.setValue(level);
      });

    this.standbyMode.valueChanges
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((standbyMode) => {
        const isManualChange = this.standbyMode.dirty;

        if (isManualChange && standbyMode === true) {
          this._isManualStandbyOverride = true;
          this.authLevel.setValue('A');
          return;
        }

        if (this._isManualStandbyOverride && standbyMode !== true) {
          this._isManualStandbyOverride = false;

          const taskFrequency = this.taskFrequency.value;
          const percentOverdue = this.percentOverdue.value;
          const consequenceRanking = this.consequenceRanking.value;

          if (taskFrequency && percentOverdue) {
            const level = this._getAuthLevel(taskFrequency, percentOverdue, consequenceRanking);
            this.authLevel.setValue(level);
          }
        }
      });

    this.e1Table.valueChanges
      .pipe(
        filter(() => !this._isLoadingData),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => this._defineRanking());

    this.gePredixTable.valueChanges
      .pipe(
        filter(() => !this._isLoadingData),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => this._defineRanking());

    this._store.page$
      .pipe(
        filter((page) => page === this.step && !this._isLoadingData),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        this._restoreDraftIfNeeded('page-enter');
        this._store.setPageValidity({ page: this.step, isStepValid: this._isEquipmentStepValid() });
      });

    this.form.valueChanges
      .pipe(
        filter(() => this.step === this.currentStep()),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        this._validateJustification();
        const isFormValid = this._isEquipmentStepValid();

        // Keep a local draft in the store so Action Items navigation does not wipe user-entered data
        this._store.setEquipment(EquipmentTabDto.fromJS(this.form.getRawValue()));
        this._cacheDraft(this.form.get('generalTabId')?.value ?? this.form.value.generalTabId, this.form.getRawValue());

        this._debug('form:valueChanges', {
          isFormValid,
          dirty: this.form.dirty,
          cr: this.consequenceRanking.value,
          e1Count: this.e1Table.length,
          predixCount: this.gePredixTable.length,
        });

        // Enqueue whenever the step is valid. Programmatic loads use emitEvent:false and are
        // additionally guarded by _isLoadingData, so this does not auto-save on data load.
        if (isFormValid) {
          const payload: ReportTask = {
            page: this.step,
            request: this._save()
          };

          if (this._bulkSave.includes(this.step)) {
            this._bulkSave.update(payload);
          } else {
            this._bulkSave.add(payload);
          }
        }

        this._store.setPageValidity({ page: this.step, isStepValid: isFormValid });
      });

    this._store.reportType$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((reportType) => {
        if (reportType === ReportType.TaskDeferral) {
          this.taskFrequency.addValidators(Validators.required);
          this.percentOverdue.addValidators(Validators.required);
          this.authLevel.addValidators(Validators.required);
          this.standbyMode.addValidators(Validators.required);
        } else {
          this.taskFrequency.setValidators(null);
          this.percentOverdue.setValidators(null);
          this.authLevel.setValidators(null);
          this.standbyMode.setValidators(null);
        }

        this.taskFrequency.updateValueAndValidity({ emitEvent: false });
        this.percentOverdue.updateValueAndValidity({ emitEvent: false });
        this.authLevel.updateValueAndValidity({ emitEvent: false });
        this.standbyMode.updateValueAndValidity({ emitEvent: false });
        this._validateJustification();
        this.form.updateValueAndValidity({ emitEvent: false });
        this._store.setPageValidity({ page: this.step, isStepValid: this._isEquipmentStepValid() });
      });

    this._apiClient.facilityOwnerAll()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(res => {
        this._facilityOwners = res ?? [];
      })
  }

  getErrorMessage(fieldName: string): string | null {
    return getErrorMessage(this.form, fieldName, this.displayNames);
  }

  private _compareEquipmentLevelRankings(a: string | null, b: string | null) {
    const defaultHighValue = 10;
    const aCode =
      !a?.includes('NotAssigned') && a?.slice(0, 2) === 'IC' ? 1 : 0;
    const bCode =
      !b?.includes('NotAssigned') && b?.slice(0, 2) === 'IC' ? 1 : 0;
    const aValue = a && !a.includes('NotAssigned') ? +a.slice(2) : defaultHighValue;
    const bValue = b && !b.includes('NotAssigned') ? +b.slice(2) : defaultHighValue;

    if (aValue < bValue) {
      return a;
    } else if (aValue > bValue) {
      return b;
    } else if (aValue === bValue) {
      return aCode > bCode ? a : b;
    }

    return null;
  }

  private _getHightRank(data: string[]) {
    if (!data?.length) {
      return 'NotAssigned';
    }

    const isTDR = this.isTDR();
    const tdrAllowedRankns = ['IC1', 'IC2', 'IC3', 'AC1', 'AC2'];
    const ranks = isTDR
      ? data.filter((item) => tdrAllowedRankns.includes(item))
      : data;

    if (!ranks.length) {
      return 'NotAssigned';
    }

    let min = ranks[0];

    for (let i = 1; i < ranks.length; i++) {
      if (!ranks[i].includes('AC') && !ranks[i].includes('IC')) {
        continue;
      }

      const minRankCode = min.slice(0, 2) === 'IC' ? 1 : 0;
      const minRankValue = +min.slice(2);

      const rankCode = ranks[i].slice(0, 2) === 'IC' ? 1 : 0;
      const rankValue = +ranks[i].slice(2);

      if (
        minRankValue > rankValue ||
        minRankValue === rankValue && minRankCode < rankCode
      ) {
        min = ranks[i];
      }
    }

    return min;
  }

  private _defineRanking(): void {
    // Skip automatic ranking calculation during data load from server
    // This prevents CR from being overwritten with empty/incomplete data
    if (this._isLoadingData) {
      return;
    }
    
    const e1 = this.e1Table.value;
    const predix = this.gePredixTable.value;
    const ranks: string[] = [];
    const rankingList: { assetConsequence: string, integrityCriticality: string }[] = [];

    e1.forEach(item => {
      rankingList.push({
        assetConsequence: item.assetConsequence,
        integrityCriticality: item.integrityCriticality,
      });
    });

    predix.forEach(item => {
      rankingList.push({
        assetConsequence: item.assetConsequence,
        integrityCriticality: item.integrityConsequence,
      });
    });


    rankingList.forEach((item) => {
      const ac = item.assetConsequence as string;
      const ic = item.integrityCriticality as string;

      const rank = this._compareEquipmentLevelRankings(ac, ic);

      if (rank) {
        ranks.push(rank);
      }
    });

    const facility = e1?.[0]?.facility;
    const area = e1?.[0]?.area;

    // Keep CR in sync with equipment selection.
    // - Equipment with only "Not Assigned" ranking should set CR to "NotAssigned".
    // - Removing the last equipment must clear stale rankings like AC2/IC2.
    //   Use "NotAssigned" as the fallback so the mandatory field stays consistent.
    const patchData: Record<string, any> = { area, facility };
    const derivedRanking = ranks.length > 0
      ? this._getHightRank(ranks)
      : 'NotAssigned';

    const resolvedCR = this._normalizeConsequenceRanking(derivedRanking) ?? 'NotAssigned';
    const currentCR = this.consequenceRanking.value;

    // Preserve a user-selected CR when derived ranking resolves to NotAssigned.
    // This avoids silently wiping an explicit user choice when equipment data
    // does not provide a meaningful ranking for calculation.
    if (resolvedCR !== 'NotAssigned' || !currentCR) {
      patchData['consequenceRanking'] = resolvedCR;
    }

    this.form.patchValue(patchData);

    // A meaningful ranking derived from user-added equipment is a real change; mark dirty so it enqueues.
    if (patchData['consequenceRanking'] && patchData['consequenceRanking'] !== 'NotAssigned') {
      this.form.markAsDirty();
    }

    this._cacheDraft(this.form.get('generalTabId')?.value ?? this.form.value.generalTabId, this.form.getRawValue());
    this._debug('defineRanking', {
      derivedCr: patchData['consequenceRanking'],
      preservedCr: !('consequenceRanking' in patchData) ? currentCR : undefined,
      finalCr: this.consequenceRanking.value,
      isTDR: this.isTDR(),
      e1Count: this.e1Table.length,
      predixCount: this.gePredixTable.length,
    });
  }

  private _cacheKey(generalTabId?: string): string | null {
    return generalTabId ? `fdm:equipment-draft:${generalTabId}` : null;
  }

  private _cacheDraft(generalTabId: string | undefined, value: any): void {
    const key = this._cacheKey(generalTabId);

    if (!key) {
      return;
    }

    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      this._debug('cacheDraft:error', { generalTabId, error });
    }
  }

  private _readDraft(generalTabId: string | undefined): EquipmentTabDto | null {
    const key = this._cacheKey(generalTabId);

    if (!key) {
      return null;
    }

    try {
      const raw = sessionStorage.getItem(key);

      if (!raw) {
        return null;
      }

      return this._normalizeEquipmentSafeguard(EquipmentTabDto.fromJS(JSON.parse(raw)));
    } catch (error) {
      this._debug('readDraft:error', { generalTabId, error });
      return null;
    }
  }

  private _isEquipmentDataEmpty(data: EquipmentTabDto | null | undefined): boolean {
    if (!data) {
      return true;
    }

    const hasCr = !!this._normalizeConsequenceRanking(data.consequenceRanking);
    const hasFacility = !!data.facility;
    const hasArea = !!data.area;
    const hasE1 = (data.e1DataEquipments?.length ?? 0) > 0;
    const hasPredix = (data.predixDataEquipments?.length ?? 0) > 0;

    return !(hasCr || hasFacility || hasArea || hasE1 || hasPredix);
  }

  private _hasRequiredEquipmentFields(data: EquipmentTabDto | null | undefined): boolean {
    if (!data) {
      return false;
    }

    const hasFacility = !!data.facility;
    const owner = data.facilityOwner as any;
    const hasFacilityOwner = !!data.facilityOwnerId
      || !!owner?.id
      || !!owner?.facilityOwnerId
      || !!owner?.name;

    return hasFacility && hasFacilityOwner;
  }

  private _normalizeEquipmentSafeguard(data: EquipmentTabDto | null | undefined): EquipmentTabDto | null {
    if (!data) {
      return null;
    }

    data.isEquipmentSafeguard = this._toEquipmentSafeguard(data.isEquipmentSafeguard);

    return data;
  }

  private _toEquipmentSafeguard(value: unknown): EquipmentSafeguard {
    if (value === EquipmentSafeguard.Unknown
      || value === EquipmentSafeguard.Yes
      || value === EquipmentSafeguard.No) {
      return value;
    }

    // Support legacy numeric drafts (0/1/2) saved before enum became string-based.
    if (value === 0 || value === '0') {
      return EquipmentSafeguard.Unknown;
    }
    if (value === 1 || value === '1') {
      return EquipmentSafeguard.Yes;
    }
    if (value === 2 || value === '2') {
      return EquipmentSafeguard.No;
    }

    return EquipmentSafeguard.Unknown;
  }

  private _restoreDraftIfNeeded(reason: string): void {
    const generalTabId = this.form.get('generalTabId')?.value
      ?? this.form.value.generalTabId
      ?? this._route.snapshot.paramMap.get('id')
      ?? undefined;

    const current = EquipmentTabDto.fromJS(this.form.getRawValue());

    if (!this._isEquipmentDataEmpty(current)) {
      this._debug('restoreDraftIfNeeded:skip_non_empty', {
        reason,
        generalTabId,
        cr: current.consequenceRanking,
        e1Count: current.e1DataEquipments?.length ?? 0,
        predixCount: current.predixDataEquipments?.length ?? 0,
      });
      return;
    }

    const storedEquipment = this._normalizeEquipmentSafeguard(this._store.equipment());
    const cachedEquipment = this._normalizeEquipmentSafeguard(this._readDraft(generalTabId));

    const fromStore =
      storedEquipment
      && storedEquipment.generalTabId === generalTabId
      && this._hasRequiredEquipmentFields(storedEquipment);

    const candidate = fromStore
      ? storedEquipment
      : (this._hasRequiredEquipmentFields(cachedEquipment) ? cachedEquipment : null);

    if (!candidate) {
      this._debug('restoreDraftIfNeeded:no_candidate', { reason, generalTabId });
      return;
    }

    this._isLoadingData = true;

    this.form.patchValue(candidate, { emitEvent: false });
    this._validateJustification();

    this.e1Table.clear({ emitEvent: false });
    candidate.e1DataEquipments?.forEach((item) => this.e1Table.push(this._fb.group(item), { emitEvent: false }));

    this.gePredixTable.clear({ emitEvent: false });
    candidate.predixDataEquipments?.forEach((item) => this.gePredixTable.push(this._fb.group(item), { emitEvent: false }));

    this._isLoadingData = false;
    this.form.updateValueAndValidity({ emitEvent: false });
    this._store.setEquipment(EquipmentTabDto.fromJS(this.form.getRawValue()));
    this._cacheDraft(generalTabId, this.form.getRawValue());

    this._debug('restoreDraftIfNeeded:applied', {
      reason,
      source: fromStore ? 'store' : 'cache',
      generalTabId,
      cr: this.consequenceRanking.value,
      e1Count: this.e1Table.length,
      predixCount: this.gePredixTable.length,
    });
  }

  private _applyReadonlyFieldState(): void {
    this.readonlyFields.forEach((fieldName) => {
      this.form.get(fieldName)?.disable({ emitEvent: false });
    });
  }

  private _debug(event: string, data?: any): void {
    return;
  }
}
